import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { LANGUAGES } from '../lib/i18n';

const PREF_KEY = 'mt_lang';

// Labels shown before i18n is loaded — must be static
const WELCOME: Record<string, string> = {
  en: 'Choose your language',
  it: 'Scegli la tua lingua',
  es: 'Elige tu idioma',
  tr: 'Dilinizi seçin',
  el: 'Επιλέξτε γλώσσα',
  ar: 'اختر لغتك',
  hi: 'अपनी भाषा चुनें',
  bn: 'আপনার ভাষা নির্বাচন করুন',
  ru: 'Выберите язык',
  id: 'Pilih bahasa Anda',
  sw: 'Chagua lugha yako',
  nl: 'Kies uw taal',
  pl: 'Wybierz język',
  uk: 'Оберіть мову',
  vi: 'Chọn ngôn ngữ của bạn',
  th: 'เลือกภาษาของคุณ',
  ko: '언어 선택',
  ms: 'Pilih bahasa anda',
};

const CONTINUE: Record<string, string> = {
  en: 'Continue',
  it: 'Continua',
  es: 'Continuar',
  tr: 'Devam et',
  el: 'Συνέχεια',
  ar: 'متابعة',
  hi: 'जारी रखें',
  bn: 'চালিয়ে যান',
  ru: 'Продолжить',
  id: 'Lanjutkan',
  sw: 'Endelea',
  nl: 'Doorgaan',
  pl: 'Kontynuuj',
  uk: 'Продовжити',
  vi: 'Tiếp tục',
  th: 'ดำเนินการต่อ',
  ko: '계속',
  ms: 'Teruskan',
};

export default function LanguageWelcomeModal() {
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [suggested, setSuggested] = useState<string>('en');
  const [selected, setSelected] = useState<string>('en');

  useEffect(() => {
    // Only show if user has never explicitly chosen a language
    if (localStorage.getItem(PREF_KEY)) return;

    let cancelled = false;

    const detect = async () => {
      let lang = 'en';

      try {
        const res = await fetch('/api/public/geo');
        if (res.ok) {
          const data = await res.json() as { lang: string };
          if (data.lang) lang = data.lang;
        }
      } catch {
        // Fallback: parse navigator.language
        const nav = navigator.language || '';
        const code = nav.split('-')[0].toLowerCase();
        const supported = LANGUAGES.map(l => l.code);
        if (supported.includes(code)) lang = code;
      }

      if (!cancelled) {
        setSuggested(lang);
        setSelected(lang);
        // Pre-apply language visually before user confirms
        i18n.changeLanguage(lang);
        setVisible(true);
      }
    };

    detect();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = () => {
    i18n.changeLanguage(selected);
    localStorage.setItem(PREF_KEY, selected);
    // Also set cookie for 1 year
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${PREF_KEY}=${selected};expires=${expires};path=/;SameSite=Lax`;
    setVisible(false);
  };

  if (!visible) return null;

  const isSuggested = selected === suggested;
  const heading = WELCOME[suggested] || WELCOME.en;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-5 max-h-[90vh]
                      animate-[fadeSlideUp_0.25s_ease-out]">

        {/* Icon + heading */}
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-11 h-11 rounded-full bg-stone-100 flex items-center justify-center">
            <Globe size={22} className="text-stone-600" />
          </div>
          <p className="text-stone-900 font-semibold text-lg leading-snug">{heading}</p>
          {isSuggested && (
            <p className="text-stone-500 text-sm">
              Based on your location
            </p>
          )}
        </div>

        {/* Language grid */}
        <div className="grid grid-cols-1 gap-2 overflow-y-auto -mx-1 px-1" style={{ scrollbarWidth: 'thin' }}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => setSelected(lang.code)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium
                          transition-all duration-150 text-left
                          ${selected === lang.code
                            ? 'border-stone-900 bg-stone-900 text-white'
                            : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
                          }`}
            >
              <span className="text-xl leading-none">{lang.flag}</span>
              <span className="flex-1">{lang.label}</span>
              {lang.code === suggested && lang.code !== selected && (
                <span className="text-xs text-stone-400 font-normal">suggested</span>
              )}
              {lang.code === suggested && lang.code === selected && (
                <span className="text-xs text-stone-300 font-normal">suggested</span>
              )}
            </button>
          ))}
        </div>

        {/* Confirm */}
        <button
          onClick={confirm}
          className="w-full bg-stone-900 hover:bg-stone-700 text-white font-semibold
                     py-3 rounded-xl transition-colors text-sm"
        >
          {CONTINUE[selected] || CONTINUE.en}
        </button>
      </div>
    </div>
  );
}
